import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { collection, doc, getDocs, updateDoc, setDoc, addDoc, serverTimestamp } from "firebase/firestore"; // Adicionado addDoc e serverTimestamp
import { db, saveLog } from "../lib/firebase"; 

import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import Navbar from "../components/ui/Navbar"; 
import AdminBar from "../components/ui/adminbar"; 
import { toast } from "sonner";

import {
  Users, UserCog, Search, Ban, ShieldAlert, 
  CheckCircle, MessageSquareWarning, Loader2, Gavel
} from "lucide-react";

export default function AdminSuspects() {
  const navigate = useNavigate();
  const { userProfile, authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [suspects, setSuspects] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // FUNÇÃO AUXILIAR PARA DISPARAR NOTIFICAÇÃO
  const sendNotification = async (targetUserId, title, message, type, category, link = "") => {
    try {
      await addDoc(collection(db, 'notifications'), {
        userId: targetUserId,
        title,
        message,
        type, // 'technician' (Laranja) ou 'client' (Azul)
        category, // 'punishment'
        link,
        read: false,
        createdAt: serverTimestamp()
      });
    } catch (error) {
      console.error("Erro ao enviar aviso de punição:", error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [ordersSnap, usersSnap] = await Promise.all([
        getDocs(collection(db, "orders")),
        getDocs(collection(db, "users"))
      ]);

      const allOrders = ordersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const allUsers = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      const now = new Date();
      const fifteenDaysAgo = new Date(now.getTime() - (15 * 24 * 60 * 60 * 1000));

      const pairs = {};
      allOrders.forEach(order => {
        if (!order.technicianId || !order.clientId) return;
        
        const createdAt = order.createdAt ? new Date(order.createdAt) : null;
        if (!createdAt || createdAt < fifteenDaysAgo) return;

        const pairKey = `${order.technicianId}_${order.clientId}`;
        if (!pairs[pairKey]) {
          pairs[pairKey] = {
            techId: order.technicianId,
            clientId: order.clientId,
            count: 0,
            orders: []
          };
        }
        pairs[pairKey].count += 1;
        pairs[pairKey].orders.push(order);
      });

      const suspectList = Object.values(pairs)
        .filter(p => p.count >= 5)
        .map(p => {
          const tech = allUsers.find(u => u.id === p.techId);
          const client = allUsers.find(u => u.id === p.clientId);
          return {
            ...p,
            techName: tech?.professionalData?.name || tech?.name || "Desconhecido",
            clientName: client?.name || "Desconhecido",
            isReincidente: tech?.fraudWarnings && tech.fraudWarnings > 0,
            warningsCount: tech?.fraudWarnings || 0,
            techEmail: tech?.email
          };
        });

      setSuspects(suspectList);
    } catch (error) {
      toast.error("Erro ao processar suspeitas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleWarn = async (suspect) => {
    if (!window.confirm(`Enviar aviso de suspensão de 3 dias para ${suspect.techName}?`)) return;
    
    try {
      const techRef = doc(db, "users", suspect.techId);
      const suspendedUntil = new Date(new Date().getTime() + (3 * 24 * 60 * 60 * 1000)).toISOString();
      const reason = "Suspeita de autoavaliação/fraude de OS (5+ OS do mesmo cliente em 15 dias)";
      
      await updateDoc(techRef, {
        isSuspended: true,
        suspendedUntil: suspendedUntil,
        fraudWarnings: (suspect.warningsCount || 0) + 1,
        lastWarningReason: reason
      });

      // NOTIFICAR O TÉCNICO SOBRE A SUSPENSÃO (LARANJA)
      await sendNotification(
        suspect.techId,
        "Conta Suspensa por 3 Dias",
        `Identificamos um padrão incomum de atividades. Motivo: ${reason}`,
        "technician",
        "punishment",
        "/profile-tech"
      );

      await saveLog(userProfile, "SUSPENSÃO POR FRAUDE", `O técnico ${suspect.techName} foi suspenso por 3 dias por suspeita de fraude com o cliente ${suspect.clientName}.`);

      toast.success("Técnico notificado e suspenso por 3 dias!");
      loadData();
    } catch (e) { toast.error("Erro ao aplicar aviso."); }
  };

  const handleBan = async (suspect) => {
    const reason = window.prompt("MOTIVO DO BANIMENTO DEFINITIVO:");
    if (!reason) return;

    try {
      const techRef = doc(db, "users", suspect.techId);
      const blacklistRef = doc(db, "blacklist", suspect.techEmail.toLowerCase());

      await updateDoc(techRef, { 
        isBlocked: true, 
        blocked: true, 
        blockReason: reason, 
        status: 'banned' 
      });

      await setDoc(blacklistRef, { 
        email: suspect.techEmail.toLowerCase(), 
        reason: "REINCIDÊNCIA EM FRAUDE: " + reason, 
        bannedAt: new Date().toISOString() 
      });

      // NOTIFICAR SOBRE O BANIMENTO (ÚLTIMO AVISO)
      await sendNotification(
        suspect.techId,
        "CONTA BANIDA PERMANENTEMENTE",
        `Sua conta foi encerrada por reincidência em fraude. Motivo: ${reason}`,
        "technician",
        "punishment"
      );

      await saveLog(userProfile, "BANIMENTO POR FRAUDE", `O técnico ${suspect.techName} foi BANIDO PERMANENTEMENTE por reincidência de fraude.`);

      toast.success("Técnico banido permanentemente!");
      loadData();
    } catch (e) { toast.error("Erro ao banir."); }
  };

  const filteredSuspects = suspects.filter(s => 
    s.techName.toLowerCase().includes(search.toLowerCase()) || 
    s.clientName.toLowerCase().includes(search.toLowerCase())
  );

  if (authLoading) return <div className="h-screen flex items-center justify-center font-black text-slate-400 uppercase tracking-widest text-left">Sincronizando Antifraude...</div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="flex pt-[73px]">
        <AdminBar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="flex-1 p-6 lg:p-10 max-w-7xl mx-auto w-full text-left">
          <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 text-left">
            <div>
              <h1 className="text-4xl font-black text-slate-900 uppercase italic tracking-tighter leading-none">Antifraude <span className="text-red-600">Suspeitos</span></h1>
              <p className="text-[10px] font-black text-slate-400 mt-2 flex items-center gap-2 uppercase tracking-widest leading-none text-left">
                <ShieldAlert size={14} className="text-red-500 animate-pulse" /> Monitoramento de padrões (Janela de 15 dias)
              </p>
            </div>
            <div className="relative w-full md:w-96 group text-left">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={20} />
              <input 
                type="text" 
                placeholder="BUSCAR TÉCNICO OU CLIENTE..." 
                className="w-full h-14 pl-12 pr-6 bg-white border-none shadow-xl rounded-2xl font-bold text-sm uppercase italic outline-none focus:ring-2 focus:ring-red-500/20 transition-all text-left" 
                value={search} 
                onChange={(e) => setSearch(e.target.value)} 
              />
            </div>
          </header>

          <div className="grid gap-6 text-left">
            {loading ? (
                <div className="py-20 text-center font-black text-slate-300 animate-pulse tracking-widest uppercase">Processando padrões de rede...</div>
            ) : filteredSuspects.length === 0 ? (
                <div className="py-24 text-center bg-white rounded-[3rem] border-2 border-dashed border-slate-200 uppercase text-left">
                    <CheckCircle size={48} className="mx-auto text-green-200 mb-4" />
                    <p className="font-black text-slate-300 uppercase italic text-center">Nenhuma atividade suspeita detectada.</p>
                </div>
            ) : filteredSuspects.map((suspect, idx) => (
                <Card key={idx} className={`rounded-[2.5rem] border-none shadow-xl overflow-hidden border-l-8 ${suspect.isReincidente ? 'border-red-600 bg-red-50/20' : 'border-orange-500'}`}>
                  <CardContent className="p-8">
                    <div className="flex flex-col xl:flex-row items-center justify-between gap-8 text-left">
                      
                      <div className="flex-1 space-y-4 text-left">
                        <div className="flex items-center gap-3">
                           <Badge className={`${suspect.isReincidente ? 'bg-red-600' : 'bg-orange-500'} text-white border-none font-black italic text-[9px] px-3 tracking-widest`}>
                             {suspect.isReincidente ? 'REINCIDENTE' : 'SUSPEITA INICIAL'}
                           </Badge>
                           <span className="text-[10px] font-black text-slate-400 uppercase italic">Volume: {suspect.count} OS identificadas</span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left text-left">
                           <div className="p-4 bg-white rounded-2xl border border-slate-100 shadow-sm text-left">
                              <Label className="text-[9px] font-black uppercase text-slate-400 mb-2 block italic tracking-widest text-left">Profissional Sob Análise</Label>
                              <div className="flex items-center gap-3">
                                 <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-white"><UserCog size={20}/></div>
                                 <p className="font-black text-slate-900 uppercase italic text-sm">{suspect.techName}</p>
                              </div>
                           </div>
                           <div className="p-4 bg-white rounded-2xl border border-slate-100 shadow-sm text-left">
                              <Label className="text-[9px] font-black uppercase text-slate-400 mb-2 block italic tracking-widest text-left">Origem (Cliente)</Label>
                              <div className="flex items-center gap-3">
                                 <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600"><Users size={20}/></div>
                                 <p className="font-black text-slate-900 uppercase italic text-sm">{suspect.clientName}</p>
                              </div>
                           </div>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3 shrink-0 text-left">
                         <div className="flex flex-col gap-1 mr-4 text-right justify-center">
                            <span className="text-[8px] font-black text-slate-400 uppercase">Alertas Prévios</span>
                            <span className={`text-xl font-black italic ${suspect.warningsCount > 0 ? 'text-red-600' : 'text-slate-300'}`}>{suspect.warningsCount}</span>
                         </div>
                         <Button 
                           onClick={() => handleWarn(suspect)} 
                           className="h-14 px-8 bg-orange-500 hover:bg-orange-600 text-white font-black rounded-2xl uppercase italic text-[10px] gap-2 shadow-lg transition-all active:scale-95"
                         >
                            <MessageSquareWarning size={18} /> SUSPENDER 3 DIAS
                         </Button>
                         <Button 
                           onClick={() => handleBan(suspect)} 
                           className="h-14 px-8 bg-red-600 hover:bg-red-700 text-white font-black rounded-2xl uppercase italic text-[10px] gap-2 shadow-lg transition-all active:scale-95"
                         >
                            <Ban size={18} /> BANIR CONTA
                         </Button>
                      </div>

                    </div>
                  </CardContent>
                </Card>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
}